import type React from "react"
import { DEFAULT_LIGHT_THEME } from "../runtime/design-tokens"

type TypographyToken = {
  size?: string | number
  weight?: string | number
  lineHeight?: string | number
  family?: string
}

export type RendererDesignTokens = {
  colors?: Record<string, string>
  spacing?: Record<string, string | number>
  typography?: Record<string, TypographyToken>
  radius?: Record<string, string | number> | string
  shadow?: Record<string, string>
}

const TOKEN_REF_RE = /^\$(color|space|radius|type|shadow|z)\.([A-Za-z0-9._-]+)$/
const CSS_COLOR_RE = /^(#|rgb\(|rgba\(|hsl\(|hsla\(|oklch\(|color\(|var\()/

const DEFAULT_SPACING: Record<string, string> = {
  "0": "0px",
  "1": "4px",
  "2": "8px",
  "3": "12px",
  "4": "16px",
  "5": "20px",
  "6": "24px",
  "8": "32px",
  "10": "40px",
  "12": "48px",
  "16": "64px",
  "20": "80px",
  "24": "96px",
  "32": "128px",
  xs: "4px",
  sm: "12px",
  md: "16px",
  lg: "24px",
  xl: "32px",
  "2xl": "48px",
}

const DEFAULT_RADIUS: Record<string, string> = {
  none: "0px",
  sm: "4px",
  base: "6px",
  md: "8px",
  lg: "12px",
  xl: "16px",
  "2xl": "24px",
  "3xl": "32px",
  full: "9999px",
}

const DEFAULT_TYPE: Record<string, TypographyToken> = {
  xs: { size: "12px", lineHeight: "16px" },
  sm: { size: "14px", lineHeight: "20px" },
  base: { size: "16px", lineHeight: "24px" },
  lg: { size: "18px", lineHeight: "28px" },
  xl: { size: "20px", lineHeight: "28px" },
  "2xl": { size: "24px", lineHeight: "32px" },
  "3xl": { size: "30px", lineHeight: "36px" },
  "4xl": { size: "36px", lineHeight: "40px" },
  "5xl": { size: "48px", lineHeight: "1" },
  "6xl": { size: "60px", lineHeight: "1" },
}

const DEFAULT_SHADOW: Record<string, string> = {
  none: "none",
  sm: "0 1px 2px rgb(0 0 0 / 0.05)",
  base: "0 1px 3px rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
  md: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
  lg: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
  xl: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
}

const TYPE_PROPERTY_PART: Record<string, keyof TypographyToken> = {
  fontSize: "size",
  lineHeight: "lineHeight",
  fontWeight: "weight",
  fontFamily: "family",
}

export function resolveStyleTokens(
  style: React.CSSProperties | Record<string, unknown> | undefined,
  tokens?: RendererDesignTokens,
): React.CSSProperties {
  if (!style) return {}
  const resolved: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(style)) {
    resolved[key] = resolveStyleTokenValue(value, tokens, key)
  }
  return resolved as React.CSSProperties
}

export function resolveStyleTokenValue(
  value: unknown,
  tokens?: RendererDesignTokens,
  cssProperty?: string,
): unknown {
  const rawValue = unwrapRawValue(value)
  if (typeof rawValue !== "string") return rawValue
  const match = TOKEN_REF_RE.exec(rawValue.trim())
  if (!match) return rawValue

  const [, group, path] = match
  if (group === "color") return resolveColorToken(path, tokens)
  if (group === "space") return resolveFromMap(tokens?.spacing, DEFAULT_SPACING, path)
  if (group === "radius") return resolveRadiusToken(path, tokens)
  if (group === "type") return resolveTypeToken(path, tokens, cssProperty)
  if (group === "shadow") return resolveFromMap(tokens?.shadow, DEFAULT_SHADOW, path)
  return cssVarFallback(group, path)
}

export function resolveColorToken(path: string, tokens?: RendererDesignTokens): string {
  const value = readTokenPath(tokens?.colors, path) ?? readTokenPath(DEFAULT_LIGHT_THEME, path)
  if (typeof value === "string" && value.trim()) {
    return normalizeColorValue(value.trim())
  }
  return cssVarFallback("color", path)
}

function resolveRadiusToken(path: string, tokens?: RendererDesignTokens): string | number {
  if (typeof tokens?.radius === "string") return tokens.radius
  return resolveFromMap(tokens?.radius, DEFAULT_RADIUS, path)
}

function resolveTypeToken(path: string, tokens?: RendererDesignTokens, cssProperty?: string): unknown {
  const token = readTokenPath(tokens?.typography, path) ?? readTokenPath(DEFAULT_TYPE, path)
  if (token == null || typeof token !== "object" || Array.isArray(token)) return cssVarFallback("type", path)
  const part = cssProperty ? TYPE_PROPERTY_PART[cssProperty] : undefined
  if (part && (token as TypographyToken)[part] != null) return (token as TypographyToken)[part]
  return (token as TypographyToken).size ?? cssVarFallback("type", path)
}

function resolveFromMap(
  tokenMap: Record<string, string | number> | undefined,
  fallbackMap: Record<string, string | number>,
  path: string,
): string | number {
  const value = readTokenPath(tokenMap, path) ?? readTokenPath(fallbackMap, path)
  return typeof value === "string" || typeof value === "number" ? value : cssVarFallback("token", path)
}

function unwrapRawValue(value: unknown): unknown {
  if (!value || typeof value !== "object" || Array.isArray(value)) return value
  const record = value as Record<string, unknown>
  return record.raw === true && "value" in record ? record.value : value
}

function readTokenPath(source: unknown, path: string): unknown {
  if (!source || typeof source !== "object") return undefined
  const record = source as Record<string, unknown>
  if (path in record) return record[path]
  const camelPath = path.replace(/-([a-z])/g, (_, char: string) => char.toUpperCase())
  if (camelPath in record) return record[camelPath]
  const parts = path.split(".")
  let current: unknown = record
  for (const part of parts) {
    if (!current || typeof current !== "object" || Array.isArray(current)) return undefined
    const currentRecord = current as Record<string, unknown>
    current = currentRecord[part] ?? currentRecord[part.replace(/-([a-z])/g, (_, char: string) => char.toUpperCase())]
  }
  return current
}

function normalizeColorValue(value: string): string {
  if (CSS_COLOR_RE.test(value)) return value
  if (/^\d+(\.\d+)?\s+\d+(\.\d+)?%\s+\d+(\.\d+)?%$/.test(value)) return `hsl(${value})`
  return value
}

function cssVarFallback(group: string, path: string): string {
  const varName = path.replace(/\./g, "-")
  if (group === "color") return `var(--color-${varName}, var(--${varName}))`
  return `var(--${group}-${varName})`
}
